import Breadcrumbs from "@/components/feature/Breadcrumbs";
import StatusTag from "@/components/feature/StatusTag";
import { formatDate, formatHeat, getCategoryLabel } from "@/lib/site";
import type { Topic } from "@/types/content";

export default function TopicHero({ topic }: { topic: Topic }) {
  const categoryLabel = getCategoryLabel(topic.categoryId);

  return (
    <section className="border-b border-background-200 bg-background-100">
      <div className="px-4 py-8 md:px-8 md:py-12">
        <Breadcrumbs
          items={[
            { name: "首页", path: "/" },
            { name: "话题档案", path: "/topics" },
            { name: topic.title, path: `/topic/${topic.id}` },
          ]}
        />

        <div className="mt-6 grid gap-8 lg:grid-cols-[1.15fr_0.85fr] lg:items-start">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full border border-secondary-200 bg-secondary-50 px-3 py-1 text-xs font-medium text-secondary-800">
                {categoryLabel}
              </span>
              <StatusTag label={topic.status} tone={topic.statusTone} size="md" />
            </div>

            <h1 className="mt-4 font-heading text-2xl font-black leading-snug text-foreground-950 md:text-[34px] md:leading-[1.25]">
              {topic.title}
            </h1>

            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-[15px]">
              {topic.summary}
            </p>

            <dl className="mt-6 flex flex-wrap gap-x-8 gap-y-4 border-t border-background-200 pt-5">
              <div>
                <dt className="text-xs text-foreground-500">首次曝光</dt>
                <dd className="mt-1 text-sm font-medium text-foreground-900">
                  {formatDate(topic.publishedAt)}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-foreground-500">最近更新</dt>
                <dd className="mt-1 text-sm font-medium text-foreground-900">
                  {formatDate(topic.updatedAt)}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-foreground-500">进展节点</dt>
                <dd className="mt-1 text-sm font-medium text-foreground-900">
                  {topic.timeline.length} 个
                </dd>
              </div>
              <div>
                <dt className="text-xs text-foreground-500">当前热度</dt>
                <dd className="mt-1 inline-flex items-center gap-1 text-sm font-medium text-primary-600">
                  <i className="ri-fire-line" aria-hidden="true" />
                  {formatHeat(topic.heat)}
                </dd>
              </div>
            </dl>
          </div>

          <figure className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
            <div className="h-56 w-full md:h-64">
              <img
                src={topic.cover}
                alt={`${topic.title} 事件档案配图`}
                title={`${topic.title} - 进展追踪档案`}
                className="h-full w-full object-cover object-top"
              />
            </div>
            <figcaption className="border-t border-background-200 px-4 py-3 text-xs text-foreground-500">
              档案编号 GT-{topic.id.toUpperCase()} · 预计阅读 {topic.readMinutes} 分钟
            </figcaption>
          </figure>
        </div>
      </div>
    </section>
  );
}